import React, { useEffect, useMemo, useState } from 'react';

import { Drawer, DrawerController } from '@reckon-web/drawer';
import { Stack } from '@reckon-web/stack';
import { AutocompleteField, BaseOption } from '@reckon-web/autocomplete';
// import { useToasts } from '@reckon-web/toast';
import { Flex } from '@reckon-web/flex';
import { LoadingDots } from '@reckon-web/loading';
import { Tag } from '@reckon-web/tag';
import { Inline } from '@reckon-web/inline';
import { Notice } from '@reckon-web/notice';
import { Text } from '@reckon-web/text';
import { makeId } from '@reckon-web/utils';
import { Tooltip } from '@reckon-web/tooltip';

import type { Job } from '../../hooks/use-query-job';
import { useClientAutoComplete } from '../../hooks/use-client-auto-complete';
import { useGetEntitiesByIds } from '../../data/useGetEntitiesByIds';

import { JobUpdateErrors } from './view';
import { getArrayThumbprint } from './form';

interface IEditClientsDrawerProps {
  /** will be changed when the parent wants us to reset any changes we may have */
  reset?: Symbol;
  drawerOpen: boolean;
  onClose: () => void;
  job: Job;
  doUpdateClients: (cloudIds: string[]) => Promise<boolean>;
  externalErrors?: JobUpdateErrors;
  testId?: string;
}

type SelectedClient = {
  name: string;
  cloudId: string;
};

export const EditClientsDrawer = ({
  reset,
  drawerOpen,
  onClose,
  job,
  doUpdateClients,
  externalErrors,
  testId = `edit-clients-drawer`,
}: IEditClientsDrawerProps) => {
  const searchClients = useClientAutoComplete();

  const [isSaving, setIsSaving] = useState(false);
  // const { addToast } = useToasts();

  const [selectedClients, setSelectedClients] = useState<SelectedClient[]>();
  const selectedClientsError = selectedClients?.length
    ? ''
    : 'You must select at least one client';

  // Incoming job has entity IDs, but no actual entities.
  const getEntitiesByIdsArgs: Parameters<typeof useGetEntitiesByIds>[0] = job
    ? {
        variables: { cloudIds: job?.entities || [] },
      }
    : { skip: true };

  const {
    loading: initialGetEntitiesByIdsLoading,
    // error: errorEntities,
    data: initialGetEntitiesByIdsData,
  } = useGetEntitiesByIds(getEntitiesByIdsArgs);

  // once we do our first load of the clients, stuff 'em in state
  // from then on, we'll mutate the collection in state
  useEffect(() => {
    if (initialGetEntitiesByIdsLoading) {
      return;
    }
    setSelectedClients(
      initialGetEntitiesByIdsData
        ?.map((x) => ({
          name: x.name || '',
          cloudId: x.cloudId,
        }))
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        )
    );
  }, [initialGetEntitiesByIdsLoading, initialGetEntitiesByIdsData, reset]);

  // when the job changes, snap off a thumbprint of the ids
  const initialEntitiesThumbprint = useMemo(
    () => getArrayThumbprint(job?.entities),
    [job?.entities]
  );

  // so that we can then compare the initial thumbprint to the current selections
  // to check dirty. Yeah, we could maintian a boolean ourselves, there's only a few ways that it can be mutated
  // but the edit form did it basically this way, and it's nicer
  // this way if they remove then re-add the same client, we're not dirty, which is technically better
  const haveClientsChanged = useMemo(() => {
    const currentThumbprint = getArrayThumbprint(
      selectedClients?.map((x) => x.cloudId)
    );
    return currentThumbprint !== initialEntitiesThumbprint;
  }, [initialEntitiesThumbprint, selectedClients]);

  // We want 20 to show (arbitrary number)
  // But we also don't want any existing selections to show up
  // So, we'll request 20 + selected.length
  // Then we'll remove existing selections from the result
  // Then we'll slice the to 20 of that off and call it "the results"
  // This way, we'll only run out of results to show them when they legit select everything matching the search
  // rather than when they select the first 20 of the results
  // This would be better if the search took in an "exclude" list too. But it does not
  const doSearchClients = async (inputValue: string): Promise<BaseOption[]> => {
    const clients = await searchClients({
      includeNonEntities: false,
      searchTerm: inputValue,
      take: 20 + (selectedClients?.length || 0),
    });
    if (clients.data) {
      const unselectedClients = clients.data.filter(
        (c) => !selectedClients?.some((x) => x.cloudId === c.cloudId)
      );
      const result = unselectedClients.map((c) => {
        return {
          value: c.cloudId,
          label: c.name || '',
        };
      });
      // slice end is exclusive, and slice will return a partial array if there's less than 20 in there
      return result.slice(0, 20);
    }
    return [];
  };

  const submit = async () => {
    // if we have an error on screen, bail
    if (selectedClientsError || !selectedClients) {
      return;
    }
    setIsSaving(true);
    const saveResult = await doUpdateClients(
      selectedClients.map((x) => x.cloudId)
    );
    setIsSaving(false);
    if (saveResult) {
      onClose();
    }
  };

  return (
    <DrawerController isOpen={drawerOpen} data-testid={testId}>
      <Drawer
        type="form"
        title={`Edit clients for ${job.name}`}
        actions={{
          cancel: {
            label: 'Cancel',
            action: () => {
              onClose();
            },
          },
          confirm: {
            label: 'Save',
            action: () => {
              submit();
            },
            loading: isSaving,
          },
        }}
        shouldShowCancelConfirmationDialog={haveClientsChanged}
        /* TODO Workflow-400 once balance supports test id's, replace this ID */
        id={makeId(testId, 'edit-clients')}
      >
        <Flex direction="column" gap="medium">
          {initialGetEntitiesByIdsLoading && (
            <LoadingDots
              label="Loading clients"
              data-testid={makeId(testId, 'loading-clients')}
            />
          )}
          {!initialGetEntitiesByIdsLoading && (
            <AutocompleteField
              // {...form.fields.clients.props}
              label="Clients*"
              placeholder="Search to add clients"
              loadOptions={doSearchClients}
              // we'll never hold a value here, have to overwrite this otherwise the multiselect props don't spread properly onto an autocomplete. Understandably too
              value={null}
              invalidMessage={
                selectedClientsError || externalErrors?.entities?.join('. ')
              }
              onChange={(selectedOption) => {
                // this won't ever be null/undefined, but typescript doesn't know that
                if (!selectedOption) {
                  return;
                }

                // and add it into the selected values
                setSelectedClients((value) => {
                  const results = [
                    ...(value || []),
                    {
                      name: selectedOption.label,
                      cloudId: selectedOption.value,
                    },
                  ];
                  results.sort((a, b) =>
                    a.name.localeCompare(b.name, undefined, {
                      sensitivity: 'base',
                    })
                  );
                  return results;
                });
              }}
              /* TODO Workflow-400 once balance supports test id's, replace this ID */
              id={makeId(testId, 'search-to-add-clients')}
            />
          )}
          {/* the list of clients */}
          {!initialGetEntitiesByIdsLoading && selectedClients && (
            <Inline gap="small">
              {selectedClients.map((e) => (
                <Tooltip
                  key={e.cloudId}
                  content={<Text overflowStrategy="breakword">{e.name}</Text>}
                >
                  <Tag
                    label={e.name}
                    onRemove={() => {
                      // remove from the selected clients
                      setSelectedClients((value) =>
                        value?.filter((x) => x.cloudId !== e.cloudId)
                      );
                    }}
                    data={{ testid: makeId(testId, 'tag', e.cloudId) }}
                  />
                </Tooltip>
              ))}
            </Inline>
          )}
          {haveClientsChanged && (
            <Notice
              size="small"
              tone="cautious"
              title="Heads up"
              data-testid={makeId(testId, 'notice-cautious')}
            >
              <Stack gap="small">
                <Text size="xsmall">
                  If you remove clients from this job, tasks associated with
                  that client will be deleted when you save changes.
                </Text>
                <Text size="xsmall">
                  Also, if this job was created from a template, tasks will not
                  be created for any clients you have added.
                </Text>
              </Stack>
            </Notice>
          )}
        </Flex>
      </Drawer>
    </DrawerController>
  );
};
